"use client";
import React, { useState } from "react";
import Image from "next/image";
import { useIntl } from "react-intl";
import CurrencySymbol from "@/Components/Common/CurrencySymbol/CurrencySymbol";
import { useRecState } from "../../Orchestrator/Recoils/Events/Recoil";
import { IPopularTripsForHomePage } from "../../Orchestrator/Types/Interface/ICmsPopularTrip";
import stopManager from "../../Lib/Utils/StopManager";
import SelectDateModal from "../DestinationDetail/SelectDateModal";
import "./PopularTripsHomepage.css";
import CommonCardSlider from "../CommonCardSlider/CommonCardSlider";
import SectionCardSkeleton from "../Shared/SectionCardSkeleton/SectionCardSkeleton";
import dayjs from "dayjs";
import { goToTrainOffers } from "../DestinationDetail/navigateToTrainOffers";

const PopularTripsHomepage: React.FC = () => {
    const intl = useIntl();
    const [popularTripsForHomePage] = useRecState<IPopularTripsForHomePage[]>("popularTripsForHomePageAtom");
    const [popularTripsLoaded] = useRecState<boolean>("popularTripsLoadedAtom");
    const [selectedTrip, setSelectedTrip] = useState<IPopularTripsForHomePage | null>(null);

    if (!popularTripsLoaded) {
        return <SectionCardSkeleton count={3} />;
    }

    const handleTripSelect = (trip: IPopularTripsForHomePage) => {
        // A real "From" price already carries the day it belongs to, so skip the
        // calendar and go straight to that day's train offers. Without a price
        // (N/A) we keep the calendar so the user can pick a date themselves.
        if (trip.price != null && trip.date) {
            goToTrainOffers({
                origin: trip.origin.code,
                destination: trip.destination.code,
                date: dayjs(trip.date),
                intl,
            });
            return;
        }
        setSelectedTrip(trip);
    };

    return (
        <div>
            <CommonCardSlider
                title={intl.formatMessage({ id: "popularTripText" })}
                subTitle={intl.formatMessage({ id: "yourPopularTripsText" })}
                items={popularTripsForHomePage}
                renderItem={(trip) => <PopularTripCard trip={trip} onSelect={handleTripSelect} />}
                noRecordText={intl.formatMessage({ id: "noData" })}
            />
            {/* Same as the Destinations "Book now" flow: pick a date in the
                calendar, then continue straight to the train offers. Mounted
                conditionally so each open starts with a fresh date selection. */}
            {selectedTrip && (
                <SelectDateModal
                    open={true}
                    startStop={selectedTrip.origin.code}
                    endStop={selectedTrip.destination.code}
                    handleClose={() => setSelectedTrip(null)}
                />
            )}
        </div >
    );
};


interface IPopularTripCard {
    trip: IPopularTripsForHomePage
    onSelect: (trip: IPopularTripsForHomePage) => void
}

const PopularTripCard = (data: IPopularTripCard) => {
    const { trip, onSelect } = data;
    const intl = useIntl();

    return (
        <div className="destinations_items popular-trips-card" onClick={() => onSelect(trip)}>
            <div className="trip_card">
                <div className="popular-trip-image-container">
                    <img
                        src={trip.origin.image[0]?.url}
                        alt="trip-origin"
                        className="cover-image origin"
                        loading="lazy"
                        decoding="async"
                    />
                    <img
                        src={trip.destination.image[0]?.url}
                        alt="trip-origin"
                        className="cover-image destination"
                        loading="lazy"
                        decoding="async"
                    />
                    <img
                        src="/assets/search_icons/arrow-turn-down-left.svg"
                        alt="arrow"
                        className="arrow-icon"
                    />
                </div>

                <div className="trip-block">
                    <div className="trip-title">
                        {stopManager.getStopNameById(trip.origin.code, trip.origin.name)}{" "}
                        <span>
                            <Image
                                src="/assets/booking/arrow-right.svg"
                                width={14}
                                height={14}
                                alt="add_calendar"
                            />
                        </span>{" "}
                        {stopManager.getStopNameById(trip.destination.code, trip.destination.name)}
                    </div>
                    <div className="trip-price">
                        <div className="price-left fs-16">
                            {trip.price != null && (
                                <span className="text-center ff-inter fs-18 fw-500 color-2F3034">
                                    {intl.formatMessage({ id: "from" })}{" "}
                                </span>
                            )}
                            <strong className="text-center ff-inter fs-18 fw-600 color-2F3034">
                                <bdi dir={intl.locale === 'ar' ? 'rtl' : 'ltr'}>
                                    {trip.price != null ? (
                                        <>
                                            <CurrencySymbol /> {trip.price}
                                        </>
                                    ) : (
                                        intl.formatMessage({ id: "popularTrip.Card.Price.NotAvailable" })
                                    )}
                                </bdi>
                            </strong>
                        </div>
                        <div className="price-arrow pt-8">
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="32"
                                height="32"
                                viewBox="0 0 32 32"
                                fill="none"
                            >
                                <mask
                                    id="mask0_7105_209423"
                                    maskUnits="userSpaceOnUse"
                                    x="0"
                                    y="0"
                                    width="32"
                                    height="32"
                                >
                                    <rect
                                        width="31.6129"
                                        height="31.6129"
                                        fill="#D9D9D9"
                                    />
                                </mask>
                                <g mask="url(#mask0_7105_209423)">
                                    <path
                                        d="M16.597 15.8063L11.4599 10.6692C11.2184 10.4277 11.0977 10.1204 11.0977 9.74716C11.0977 9.37396 11.2184 9.06661 11.4599 8.82512C11.7014 8.58363 12.0087 8.46289 12.3819 8.46289C12.7551 8.46289 13.0625 8.58363 13.304 8.82512L19.3631 14.8843C19.4948 15.016 19.5881 15.1587 19.643 15.3124C19.6979 15.466 19.7253 15.6307 19.7253 15.8063C19.7253 15.9819 19.6979 16.1466 19.643 16.3003C19.5881 16.4539 19.4948 16.5966 19.3631 16.7283L13.304 22.7875C13.0625 23.029 12.7551 23.1497 12.3819 23.1497C12.0087 23.1497 11.7014 23.029 11.4599 22.7875C11.2184 22.546 11.0977 22.2387 11.0977 21.8654C11.0977 21.4922 11.2184 21.1849 11.4599 20.9434L16.597 15.8063Z"
                                        fill="#C10505"
                                    />
                                </g>
                            </svg>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default PopularTripsHomepage;
